import type { PaginatedResponse } from 'app/src/model'
import type { portalConfigurationApi } from 'app/src/services/welfareportal'
import { useWebAppContext } from 'app/src/shared/hooks'
import { clone, merge } from 'lodash'
import { Button } from 'primereact/button'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export function ListItemRenderer<T>(props: {
  data: Partial<PaginatedResponse<T>>
  renderItem: (item: T, itemIndex: number) => React.ReactNode
  loading?: boolean
  loadMore?: (
    limit: number,
    offset: number,
  ) => Promise<
    { response: PaginatedResponse<T>; responseCallBack: (data: PaginatedResponse<T>) => void | Promise<void> } | undefined
  > | void
  pt?: {
    container?: {
      className?: string
    }
    emptymessage?: {
      className?: string
    }
    item?: {
      className?: string
      seedKey?: string
    }
    loadmore?: {
      className?: string
      label?: string
    }
    root?: {
      className?: string
    }
    autofocus?: {
      disabled?: boolean
      className?: string
    }
    paginator?: Partial<portalConfigurationApi.PortalConfig['paginatorConfig']>
  }
}) {
  const prevRenderedItems = useRef<number>(0)
  const focusRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation('home', { keyPrefix: 'Global.Paginator' })
  const context = useWebAppContext()

  const applyTreshold = useCallback((value: number, treshold: number) => {
    return value < treshold ? value : treshold
  }, [])

  const paginatorConfig: portalConfigurationApi.PortalConfig['paginatorConfig'] = useMemo(
    () => merge(clone(context.portalConfiguration.paginatorConfig), props.pt?.paginator), //clone resolve Lodash merge inplace
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.pt?.paginator],
  )

  const loadMoreButton = useMemo(() => {
    const onLoadMoreClick = async () => {
      if (props.data.paging) {
        let elementToGet = (props.data.paging.total ?? 0) - (props.data.items?.length ?? 0)
        elementToGet = applyTreshold(elementToGet, paginatorConfig?.loadMorePageSize)

        if (props.loadMore && elementToGet > 0) {
          const loadMoreResponse = await props.loadMore(elementToGet, props.data?.items?.length ?? 0)
          if (loadMoreResponse) {
            await loadMoreResponse.responseCallBack({
              paging: loadMoreResponse.response.paging,
              items: [...(props.data.items ?? []), ...(loadMoreResponse.response.items ?? [])],
            })
          }
        }
      }
    }

    return props.data?.items && props.data.items.length < (props?.data?.paging?.total ?? 0) ? (
      <>
        <Button
          className={props.pt?.loadmore?.className ?? 'mx-auto mt-6 grid'}
          text
          icon='pi pi-plus'
          label={t('LoadMoreLabel')}
          loading={props.loading}
          onClick={onLoadMoreClick}
        />
      </>
    ) : (
      <></>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, t, paginatorConfig])

  return (
    <div className={props?.pt?.root?.className ?? ''}>
      <div className={props?.pt?.container?.className ?? ''}>
        {(props.data?.items?.length ?? 0) > 0 &&
          props.data?.items?.map((item: T, itemIndex: number) => (
            <div
              ref={
                (typeof props.pt?.autofocus?.disabled === 'undefined' || !props.pt.autofocus.disabled) &&
                itemIndex + 1 == prevRenderedItems?.current /* || (prevRenderedItems?.current == 0 && itemIndex == 0)*/
                  ? focusRef
                  : undefined
              }
              key={`${props.pt?.item?.seedKey ?? 'iSK'}_${itemIndex}`}
              id={`${props.pt?.item?.seedKey ?? 'iSK'}_${itemIndex}`}
              className={props.pt?.item?.className ?? ''}
            >
              {props.renderItem(item, itemIndex)}
            </div>
          ))}
      </div>
      {!props.data?.items ||
        (props.data.items.length === 0 && (
          <p className={props.pt?.emptymessage?.className ?? 'mx-auto grid text-center'}>{t('EmptyMessage')}</p>
        ))}
      {loadMoreButton}
    </div>
  )
}
